import React, { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { getApiUrl, getCsrfToken } from "../utils/apiHelper";

export default function TwoFactorModal({
  isOpen,
  email,
  onClose,
  onVerified,
}) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const maskedEmail = useMemo(() => {
    if (!email || typeof email !== "string") return "";
    const [name, domain] = email.split("@");
    if (!domain) return email;
    const safeName = name.length <= 2 ? name[0] + "*" : name.slice(0, 2) + "*".repeat(Math.max(1, name.length - 2));
    return `${safeName}@${domain}`;
  }, [email]);

  useEffect(() => {
    if (isOpen) {
      setCode("");
      setError("");
      setSubmitting(false);
      setTimeout(() => inputRef.current?.focus?.(), 0);
    }
  }, [isOpen]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    const normalizedCode = String(code || "").replace(/\D/g, "").slice(0, 6);
    if (!email) {
      setError("Missing email for verification. Please sign in again.");
      setSubmitting(false);
      return;
    }
    if (normalizedCode.length !== 6) {
      setError("Please enter the 6-digit code.");
      setSubmitting(false);
      return;
    }

    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl("/api/internal/auth/login/2fa/verify/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify({ email, code: normalizedCode }),
      });

      const data = await response.json().catch(() => ({}));
      if (data?.success) {
        onVerified?.(data);
      } else {
        setError(data?.message || "Invalid or expired verification code");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-backdrop"
      style={{ zIndex: 10000 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          disabled={submitting}
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            background: "transparent",
            border: "none",
            cursor: submitting ? "not-allowed" : "pointer",
            color: "var(--card-text)",
            opacity: submitting ? 0.6 : 1,
          }}
          aria-label="Close"
        >
          <X size={24} />
        </button>

        <h3>Two-factor verification</h3>
        <p style={{ marginTop: 0, marginBottom: 16, color: "var(--card-text)", opacity: 0.75 }}>
          Enter the 6-digit code we emailed to {maskedEmail || "your address"}.
        </p>

        {error && (
          <div className="error-message" style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleVerify}>
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label htmlFor="twofa-code">Verification code</label>
            <input
              ref={inputRef}
              id="twofa-code"
              name="twofa-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              value={code}
              onChange={(e) => {
                const next = e.target.value.replace(/\D/g, "").slice(0, 6);
                setCode(next);
                setError("");
              }}
              disabled={submitting}
              style={{
                letterSpacing: "0.2em",
                textAlign: "center",
                fontSize: 18,
              }}
            />
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn" disabled={submitting}>
              {submitting ? "Verifying..." : "Verify"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

